import eventLogger from "@services/ApiLogger/EventLogger.js";
import catalogTranslationApi from "@services/catalogGroupTranslations.js";
import catalogNodeApi from "@services/catalogNodes.js";

function defaultState() {
  return {
    masterData: {},
    path: [],
    attributes: {},
    mediaAssets: {},
    metadata: {
      loading: false,
      update: {
        seen: true
      },
      node: {
        id: "",
        catalogId: ""
      }
    },
    translatedMasterdata: {},
    translatedAttributes: {},
    translatedMediaAssets: {},
    modified: {
      masterData: false,
      attributes: false,
      mediaAssets: false,
      translatedMasterdata: false,
      translatedAttributes: false,
      translatedMediaAssets: false
    },
    updateTasks: {
      masterData: [],
      attributes: [],
      mediaAssets: [],
      translatedMasterdata: [],
      translatedAttributes: [],
      translatedMediaAssets: []
    },
    updateErrors: {
      masterData: [],
      attributes: [],
      mediaAssets: [],
      translatedMasterdata: [],
      translatedAttributes: [],
      translatedMediaAssets: []
    }
  };
}
export default {
  namespaced: true,
  state: { ...defaultState() },
  getters: {
    isSaving(state) {
      return Object.keys(state.updateTasks).some((x) => !!state.updateTasks[x].length);
    },
    hasUnsavedData(state) {
      const sections = Object.keys(state.modified);
      return sections.some((x) => state.modified[x]);
    },
    lastRunSuccess(state) {
      return Object.keys(state.updateErrors).every((x) => !state.updateErrors[x].length);
    }
  },
  mutations: {
    "refresh-masterdata"(state, arg) {
      const data = { ...state.masterData };
      data[arg.language] = arg.data;
      state.masterData = data;
    },
    setCatalogNodePath(state, arg) {
      state.path = arg;
    },
    "refresh-attributedata"(state, arg) {
      state.attributes[arg.language] = arg.data;
    },
    "refresh-catalog-node-media-assets"(state, arg) {
      state.mediaAssets[arg.language] = arg.data;
    },
    "change-node"(state, args) {
      state.metadata.node = args;
    },
    "update-loading-state"(state, isLoading) {
      state.metadata.loading = isLoading;
    },
    "update-save-status"(state, arg) {
      const section = state.updateTasks[arg.section];
      if (arg.running && section.indexOf(arg.lang) < 0) section.push(arg.lang);
      if (!arg.running) {
        const x = section.indexOf(arg.lang);
        section.splice(x, 1);
      }
      state.metadata.update.seen = arg.seen == undefined ? false : arg.seen;
      if (arg.error) {
        state.updateErrors[arg.section].push(arg.error);
        state.metadata.update.apiError = arg.apiError;
      }
      if (arg.success != undefined) state.metadata.update.success = arg.success;
    },
    "notification-seen"(state) {
      state.metadata.update.seen = true;
    },
    "mark-dirty-status"(state, args) {
      state.modified[args.section] = args.dirty;
    },
    "clear-dirty-status"(state) {
      state.modified = {
        masterData: false,
        attributes: false,
        mediaAssets: false,
        translatedMasterdata: false,
        translatedAttributes: false,
        translatedMediaAssets: false
      };
    },
    "reset-state"(state) {
      Object.assign(state, defaultState());
    },
    "reset-update-status"(state) {
      state.updateTasks = {
        masterData: [],
        attributes: [],
        mediaAssets: [],
        translatedMasterdata: [],
        translatedAttributes: [],
        translatedMediaAssets: []
      };
      state.updateErrors = {
        masterData: [],
        attributes: [],
        mediaAssets: [],
        translatedMasterdata: [],
        translatedAttributes: [],
        translatedMediaAssets: []
      };
    },
    "load-translation-masterdata"(state, arg) {
      state.translatedMasterdata = arg.data;
    },
    "load-translation-attributes"(state, arg) {
      state.translatedAttributes = arg.data;
    },
    "load-translation-mediaAssets"(state, arg) {
      state.translatedMediaAssets = arg.data;
    }
  },
  actions: {
    loadMasterData: async function (context, args) {
      const { logContext, ...rest } = args;
      logContext.info("loading catalog master data", rest);
      const data = await catalogNodeApi.getMasterData(args.nodeId, args.catalogId, args.language);
      logContext.info("loading catalog master data successful", rest);

      context.commit("refresh-masterdata", {
        language: args.language,
        data: data
      });
    },
    loadAttributeData: async function (context, args) {
      const { logContext, ...rest } = args;
      logContext.info("loading catalog attribute path", rest);
      const data = await catalogNodeApi.getAttributes(args.nodeId, args.catalogId, args.language);
      logContext.info("loading catalog attribute path successful", rest);

      context.commit("refresh-attributedata", {
        language: args.language,
        data: data
      });
    },
    loadMediaAssets: async function (context, args) {
      const { logContext, ...rest } = args;
      logContext.info("loading catalog media assets", rest);
      const data = await catalogNodeApi.getMediaAssets(rest);
      logContext.info("loading catalog media assets successful");
      context.commit("refresh-catalog-node-media-assets", {
        language: args.languageCode,
        data: data
      });
    },
    loadCatalogGroupWithLocalizations: async function ({ dispatch, rootState, commit, state }, args) {
      const logContext = eventLogger.beginContext("loadCatalogGroupWithLocalizations");
      const requestedLanguages = args.languages || rootState.metadata.contentLanguages.enabled;
      let languages = requestedLanguages
        .map((x) => [x.languageCode, x.fallbackLanguageCode])
        .flat()
        .filter((x) => !!x);
      try {
        const path = state.path;
        commit("reset-state");
        //'path' property of store will get updated when we select a node in the tree.
        //reset-state will reset the value of 'path' property to default value and the value of 'path' will be missed.
        //So the value of 'path' will be stored in a local variable and reassigned the value after reset-state.
        commit("setCatalogNodePath", path);
        commit("update-loading-state", true);
        commit("change-node", { nodeId: args.nodeId, catalogId: args.catalogId });

        languages = new Set(languages);
        logContext.info("Started catalog node loading", { languages, nodeId: args.nodeId });
        const tasks = [];
        languages.forEach((lang) => {
          tasks.push(dispatch("loadMasterData", { nodeId: args.nodeId, catalogId: args.catalogId, language: lang, logContext }));
          tasks.push(dispatch("loadAttributeData", { nodeId: args.nodeId, catalogId: args.catalogId, language: lang, logContext }));
          tasks.push(dispatch("loadMediaAssets", { nodeId: args.nodeId, catalogId: args.catalogId, languageCode: lang, logContext }));
        });
        tasks.push(dispatch("loadTranslatedCatalogGroupLocalization", { nodeId: args.nodeId, catalogId: args.catalogId, logContext }));

        await Promise.all(tasks);
        logContext.info("Completed catalog node loading successfully", { languages, nodeId: args.nodeId });
      } catch (error) {
        logContext.info("Catalog node loading failed", { languages, exception: error, nodeId: args.nodeId }).alsoToConsole();
      }
      logContext.complete();
      commit("update-loading-state", false);
    },
    loadTranslatedCatalogGroupLocalization: async function ({ dispatch, rootState }, args) {
      const requestedLanguages = args.languages || rootState.metadata.contentLanguages.enabled;
      let languages = requestedLanguages
        .map((x) => [x.languageCode, x.fallbackLanguageCode])
        .flat()
        .filter((x) => !!x);
      languages = Array.from(new Set(languages));
      const tasks = [];
      const { logContext } = args;
      logContext.info("Starting loading translation sections for catalog node");
      tasks.push(dispatch("loadTranslatedMasterData", { nodeId: args.nodeId, catalogId: args.catalogId, languages: languages, logContext }));
      tasks.push(dispatch("loadTranslatedAttributes", { nodeId: args.nodeId, catalogId: args.catalogId, languages: languages, logContext }));
      tasks.push(dispatch("loadTranslatedMediaAssets", { nodeId: args.nodeId, catalogId: args.catalogId, languages: languages, logContext }));

      try {
        await Promise.all(tasks);
        logContext.info("Completed loading translation sections for catalog node", { languages, nodeId: args.nodeId });
      } catch (error) {
        console.error("loading-complete with errors for catalog group translation", args, error);
      }
    },
    loadTranslatedMasterData: async function (context, args) {
      const model = {};
      const languages = args.languages;
      const tasks = languages.map(async (lang) => {
        const data = await catalogTranslationApi.getMasterData(args.nodeId, args.catalogId, lang);
        model[lang] = data;
      });

      try {
        await Promise.all(tasks);
        context.commit("load-translation-masterdata", {
          data: model
        });
      } catch (error) {
        console.error(error);
      }
    },
    loadTranslatedAttributes: async function (context, args) {
      const model = {};
      const languages = args.languages;
      const tasks = languages.map(async (lang) => {
        const data = await catalogTranslationApi.getAttributes(args.nodeId, args.catalogId, lang);
        model[lang] = data;
      });

      try {
        await Promise.all(tasks);
        context.commit("load-translation-attributes", {
          data: model
        });
      } catch (error) {
        console.error(error);
      }
    },
    loadTranslatedMediaAssets: async function (context, args) {
      const model = {};
      const languages = args.languages;
      const tasks = languages.map(async (lang) => {
        const data = await catalogTranslationApi.getMediaAssets(args.nodeId, args.catalogId, lang);
        model[lang] = data;
      });

      try {
        await Promise.all(tasks);
        context.commit("load-translation-mediaAssets", {
          data: model
        });
      } catch (error) {
        console.error(error);
      }
    },
    saveMasterData: async function (context, args) {
      context.commit("refresh-masterdata", {
        language: args.language,
        data: args.model
      });
      context.commit("update-save-status", { section: "masterData", lang: args.language, running: true });
      try {
        await catalogNodeApi.updateMasterData(args.language, args.model);
        context.commit("update-save-status", { section: "masterData", lang: args.language, running: false });
      } catch (error) {
        context.commit("update-save-status", {
          section: "masterData",
          lang: args.language,
          running: false,
          error: error,
          apiError: error.isAxiosError,
          success: false
        });
      }
    },
    saveCatalogNodeAttributes: async function (context, args) {
      context.commit("refresh-attributedata", {
        language: args.language,
        data: args.model
      });
      context.commit("update-save-status", { section: "attributes", lang: args.language, running: true });
      try {
        await catalogNodeApi.updateAttributeData(args.language, args.model);
        context.commit("update-save-status", { section: "attributes", lang: args.language, running: false });
      } catch (error) {
        context.commit("update-save-status", {
          section: "attributes",
          lang: args.language,
          running: false,
          error: error,
          apiError: error.isAxiosError,
          success: false
        });
      }
    },
    saveMediaAssets: async function (context, args) {
      context.commit("update-save-status", { section: "mediaAssets", running: true });

      for (let index = 0; index < args.length; index++) {
        const localizedEntry = args[index];
        context.commit("refresh-catalog-node-media-assets", {
          language: localizedEntry.language,
          data: localizedEntry.model
        });
        try {
          await catalogNodeApi.updateMediaAssets(localizedEntry.nodeId, localizedEntry.language, localizedEntry.model);
        } catch (error) {
          context.commit("update-save-status", { section: "mediaAssets", running: false, error: error, apiError: error.isAxiosError, success: false });
        }
      }

      context.commit("update-save-status", { section: "mediaAssets", running: false });
    },
    SaveTranslatedMasterdata: async function (context, args) {
      context.commit("update-save-status", { section: "translatedMasterdata", running: true });
      try {
        const languages = Object.keys(args.model);
        const tasks = languages.map((lang) => catalogTranslationApi.updateMasterdata(lang, args.model[lang]));
        await Promise.all(tasks);
        context.commit("load-translation-masterdata", {
          data: args.model
        });
      } catch (error) {
        context.commit("update-save-status", {
          section: "translatedMasterdata",
          running: false,
          error: error,
          apiError: error.isAxiosError,
          success: false
        });
      }
      context.commit("update-save-status", { section: "translatedMasterdata", running: false });
    },
    SaveTranslatedAttributes: async function (context, args) {
      context.commit("update-save-status", { section: "translatedAttributes", lang: args.languageCode, running: true });
      try {
        const languages = Object.keys(args.model);
        const tasks = languages.map((lang) => catalogTranslationApi.updateAttributeData(lang, args.model[lang]));
        await Promise.all(tasks);

        context.commit("load-translation-attributes", {
          data: args.model
        });
      } catch (error) {
        context.commit("update-save-status", {
          section: "translatedAttributes",
          running: false,
          error: error,
          apiError: error.isAxiosError,
          success: false
        });
      }
      context.commit("update-save-status", { section: "translatedAttributes", running: false });
    },
    SaveTranslatedMediaAssets: async function (context, args) {
      context.commit("update-save-status", { section: "translatedMediaAssets", lang: args.languageCode, running: true });
      try {
        const languages = Object.keys(args.model);
        const tasks = languages.map((lang) => catalogTranslationApi.updateMediaAssets(lang, args.model[lang]));
        await Promise.all(tasks);

        context.commit("load-translation-mediaAssets", {
          data: args.model
        });
      } catch (error) {
        context.commit("update-save-status", {
          section: "translatedMediaAssets",
          running: false,
          error: error,
          apiError: error.isAxiosError,
          success: false
        });
      }
      context.commit("update-save-status", { section: "translatedMediaAssets", running: false });
    }
  }
};
